import { useEffect, useState, useCallback } from "react";
import Modal from "@mui/material/Modal";
import Autocomplete from "@mui/material/Autocomplete";
import { TextField } from "@mui/material";
import { IoCloseOutline } from "react-icons/io5";
import { useTranslation } from "react-i18next";

import {
  fetchAllRoles,
  createNewShipperUser,
} from "../../../../../../../../services/settings.services";

import Countries from "../../../../../../../../data/countries";
import BoltButton from "../../../../../../../../Components/UI/Buttons/BoltButton";
import RoleDropdownWithCreate from "../RoleDropdownWithCreate";
import { useFormat } from "../../../../../../../../i18n/format";

const inputCls =
  "w-full h-10 px-3 border border-ui-border rounded-lg text-sm text-ui-text outline-none focus:border-brand focus:ring-1 focus:ring-brand-light transition-all";

const autocompleteSx = {
  "& .MuiOutlinedInput-root": {
    height: "40px",
    padding: "0 9px",
    fontSize: "14px",
    borderRadius: "8px",
    "& fieldset": { borderColor: "var(--color-ui-border)" },
    "&:hover fieldset": { borderColor: "var(--color-ui-border)" },
    "&.Mui-focused fieldset": {
      borderColor: "#60a5fa",
      borderWidth: "1px",
    },
  },
  "& .MuiAutocomplete-input": {
    padding: "0 !important",
  },
};

const emptyData = {
  username: "",
  firstname: "",
  lastname: "",
  email: "",
  phone_number: "",
  address: "",
  country: "",
  province: "",
  city: "",
  zip: "",
  role: "",
  roles: [],
  password: "",
};

const inputFields = [
  { label: "First Name", i18nKey: "fields.firstName", name: "firstname" },
  { label: "Last Name", i18nKey: "fields.lastName", name: "lastname" },
  { label: "Username", i18nKey: "fields.username", name: "username" },
  { label: "Email", i18nKey: "fields.email", name: "email", type: "email" },
  {
    label: "Password",
    i18nKey: "fields.password",
    name: "password",
    type: "password",
  },
  {
    label: "Phone Number",
    i18nKey: "fields.phoneNumber",
    name: "phone_number",
    type: "tel",
  },
  { label: "Province", i18nKey: "fields.province", name: "province" },
  { label: "City", i18nKey: "fields.city", name: "city" },
  { label: "Postal / Zip Code", i18nKey: "fields.postalZip", name: "zip" },
];

export default function AddShipperUser({ open, setOpen, setUsers, setSnack }) {
  const { t } = useTranslation("settings");
  const { formatCountry } = useFormat();
  const [data, setData] = useState(emptyData);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleClose = () => {
    setOpen(false);
    setErrors({});
    setData(emptyData);
  };

  const updateData = useCallback((field, value) => {
    setData((prev) => ({
      ...prev,
      [field]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [field]: "",
    }));
  }, []);

  const handleChange = useCallback(
    ({ target: { name, value } }) => {
      updateData(name, value);
    },
    [updateData],
  );

  const handleRoleChange = useCallback((role) => {
    setData((prev) => ({
      ...prev,
      role: role?.name ?? "",
      roles: role ? [role] : [],
    }));

    setErrors((prev) => ({
      ...prev,
      role: "",
    }));
  }, []);

  const validateForm = () => {
    const newErrors = {};

    if (!data.firstname.trim()) {
      newErrors.firstname = t("helperUsers.validation.firstNameRequired");
    }

    if (!data.lastname.trim()) {
      newErrors.lastname = t("helperUsers.validation.lastNameRequired");
    }

    if (!data.username.trim()) {
      newErrors.username = t("helperUsers.validation.usernameRequired");
    }

    if (!data.email.trim()) {
      newErrors.email = t("helperUsers.validation.emailRequired");
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(data.email)) {
      newErrors.email = t("helperUsers.validation.emailInvalid");
    }

    if (!data.password.trim()) {
      newErrors.password = t("helperUsers.validation.passwordRequired");
    } else if (data.password.length < 6) {
      newErrors.password = t("helperUsers.validation.passwordMin");
    }

    if (!data.phone_number.trim()) {
      newErrors.phone_number = t("helperUsers.validation.phoneRequired");
    }

    if (!data.country.trim()) {
      newErrors.country = t("helperUsers.validation.countryRequired");
    }

    if (!data.province.trim()) {
      newErrors.province = t("helperUsers.validation.provinceRequired");
    }

    if (!data.city.trim()) {
      newErrors.city = t("helperUsers.validation.cityRequired");
    }

    if (!data.zip.trim()) {
      newErrors.zip = t("helperUsers.validation.zipRequired");
    }

    if (!data.address.trim()) {
      newErrors.address = t("helperUsers.validation.addressRequired");
    }

    if (!data.roles.length) {
      newErrors.role = t("helperUsers.validation.roleRequired");
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleCreateNew = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    const payload = {
      ...data,
      roles: data.roles.map((role) => role.id),
    };

    delete payload.role;

    const response = await createNewShipperUser(payload);

    setLoading(false);

    if (!response?.data?.success) {
      const errData = response?.error?.response?.data ?? {};

      const fieldErrors = Object.entries(errData)
        .filter(([key]) => !["success", "message", "request"].includes(key))
        .flatMap(([, msgs]) => msgs)
        .join(" ");

      setSnack((prev) => ({
        ...prev,
        open: true,
        text: fieldErrors || errData?.message || t("messages.somethingWentWrong"),
        severity: "error",
      }));

      return;
    }

    setUsers((prev) =>
      Array.isArray(prev)
        ? [response.data.result, ...prev]
        : {
            ...prev,
            data: [response.data.result, ...(prev?.data ?? [])],
          },
    );

    setData(emptyData);
    setErrors({});
    handleClose();

    setSnack((prev) => ({
      ...prev,
      open: true,
      text: t("helperUsers.added"),
      severity: "success",
    }));
  };

  useEffect(() => {
    const getRoles = async () => {
      const saved = await fetchAllRoles();

      setRoles(
        saved?.data?.result?.data?.map((role) => ({
          id: role.id,
          name: role.name,
        })) ?? [],
      );
    };

    getRoles();
  }, []);

  const renderInput = ({ label, i18nKey, name, type = "text" }) => (
    <div key={name}>
      <label className="block text-xs font-medium text-ui-text-muted mb-1">
        {t(i18nKey, { defaultValue: label })}
      </label>

      <input
        className={`${inputCls} ${
          errors[name]
            ? "border-red-400 focus:border-red-400 focus:ring-state-danger-bg"
            : ""
        }`}
        placeholder={t(i18nKey, { defaultValue: label })}
        name={name}
        value={data[name]}
        onChange={handleChange}
        type={type}
      />

      {errors[name] && (
        <p className="text-state-danger text-xs mt-1">{errors[name]}</p>
      )}
    </div>
  );

  return (
    <Modal open={open} onClose={handleClose}>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-ui-surface rounded-xl shadow-2xl w-[680px] max-w-[95vw] max-h-[90vh] flex flex-col outline-none">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-ui-surface-sunken">
          <h2 className="text-lg font-semibold text-ui-text m-0 leading-none">
            {t("helperUsers.createTitle")}
          </h2>

          <button
            onClick={handleClose}
            className="text-ui-text-subtle hover:text-ui-text-muted transition-colors"
          >
            <IoCloseOutline size={22} />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto px-6 py-5 flex-1">
          <div className="grid grid-cols-2 gap-x-4 gap-y-4">
            {inputFields.map(renderInput)}

            {/* Country */}
            <div>
              <label className="block text-xs font-medium text-ui-text-muted mb-1">
                {t("fields.country")}
              </label>

              <Autocomplete
                disablePortal
                options={Countries}
                value={
                  Countries.find((country) => country.name === data.country) ||
                  null
                }
                getOptionLabel={(option) => formatCountry(option.code, option.name)}
                sx={autocompleteSx}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={t("fields.country")}
                    error={!!errors.country}
                    helperText={errors.country}
                  />
                )}
                onChange={(_, country) =>
                  updateData("country", country?.name ?? "")
                }
              />
            </div>

            {/* Address */}
            <div className="col-span-2">
              <label className="block text-xs font-medium text-ui-text-muted mb-1">
                {t("fields.address")}
              </label>

              <input
                className={`${inputCls} ${
                  errors.address
                    ? "border-red-400 focus:border-red-400 focus:ring-state-danger-bg"
                    : ""
                }`}
                placeholder={t("fields.address")}
                name="address"
                value={data.address}
                onChange={handleChange}
              />

              {errors.address && (
                <p className="text-state-danger text-xs mt-1">{errors.address}</p>
              )}
            </div>

            {/* Role */}
            <div className="col-span-2">
              <label className="block text-xs font-medium text-ui-text-muted mb-1">
                {t("fields.role")}
              </label>

              <RoleDropdownWithCreate
                value={data.roles?.[0] ?? null}
                onChange={handleRoleChange}
                roles={roles}
                setRoles={setRoles}
              />

              {errors.role && (
                <p className="text-state-danger text-xs mt-1">{errors.role}</p>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-6 py-3 border-t border-ui-surface-sunken">
          <BoltButton
            label={t("common:actions.cancel")}
            variant="outline"
            size="md"
            onClick={handleClose}
          />

          <BoltButton
            label={t("helperUsers.createUserSubmit")}
            variant="primary"
            size="md"
            loading={loading}
            onClick={handleCreateNew}
          />
        </div>
      </div>
    </Modal>
  );
}
